/**
 * Markdown do CRM para o HTML que o Telegram aceita.
 *
 * HTML e não `MarkdownV2`: o segundo exige escapar dezoito caracteres
 * (`_*[]()~\`>#+-=|{}.!`) em **todo** o texto, e um escape esquecido faz o
 * Telegram recusar a mensagem inteira com 400 — quem escreveu perde o texto e
 * vê um erro que não explica nada. Em HTML só três caracteres importam, eles
 * são escapados antes de qualquer coisa, e as únicas tags que existem são as
 * que esta função gera. É impossível o usuário produzir marcação inválida.
 *
 * O conjunto é o que o Telegram renderiza, nem mais nem menos: negrito,
 * itálico, sublinhado, riscado, código, bloco de código, spoiler e link.
 * Título, lista e citação não existem na Bot API — aceitar a sintaxe deles
 * aqui prometeria um resultado que não chega.
 */

/** Só estes três quebram o HTML do Telegram. */
function escaparHtml(texto: string): string {
  return texto.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
}

/**
 * Marcador de um trecho protegido.
 *
 * Usa ` `, que não sobrevive a um teclado: se ele aparecesse no texto
 * original, um `*` dentro de um bloco de código poderia virar negrito.
 */
const RESERVA = ' '

export function markdownParaHtml(entrada: string): string {
  const protegidos: string[] = []

  /** Guarda o trecho e devolve o marcador que ocupa o lugar dele. */
  const proteger = (html: string) => {
    protegidos.push(html)
    return `${RESERVA}${protegidos.length - 1}${RESERVA}`
  }

  // Código sai de cena antes de tudo: dentro dele nada é marcação, e converter
  // um `*` que o usuário escreveu para mostrar seria estragar o exemplo dele.
  let texto = entrada
    .replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang: string, corpo: string) =>
      proteger(
        lang
          ? `<pre><code class="language-${escaparHtml(lang)}">${escaparHtml(corpo.replace(/\n$/, ''))}</code></pre>`
          : `<pre>${escaparHtml(corpo.replace(/\n$/, ''))}</pre>`,
      ),
    )
    .replace(/`([^`\n]+)`/g, (_, corpo: string) => proteger(`<code>${escaparHtml(corpo)}</code>`))

  texto = escaparHtml(texto)

  // Link antes do resto: o rótulo pode ter negrito dentro, e o endereço não
  // pode ser tocado por nenhuma outra regra.
  texto = texto.replace(
    /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_, rotulo: string, url: string) => proteger(`<a href="${url}">${aplicarInline(rotulo)}</a>`),
  )

  texto = aplicarInline(texto)

  // Devolve os protegidos. Em ordem reversa porque um link protegido pode
  // conter o marcador de um código protegido antes dele.
  for (let i = protegidos.length - 1; i >= 0; i--) {
    texto = texto.replaceAll(`${RESERVA}${i}${RESERVA}`, protegidos[i] ?? '')
  }

  return texto
}

/**
 * Monta o padrão de um par de delimitadores.
 *
 * O miolo não pode começar nem terminar em espaço — é o que separa
 * `*negrito*` de `2 * 3 * 4`, onde os asteriscos são multiplicação e viravam
 * negrito. E o delimitador só abre no começo da linha, depois de espaço ou
 * depois de `(`, para `telegram_user_id` continuar sendo um nome e não um
 * itálico no meio da palavra.
 */
function par(delimitador: string, tag: string) {
  const d = delimitador.replaceAll(/[|*~]/g, '\\$&')
  // O `??` no fim é o que faz o par fechar no primeiro delimitador possível:
  // em `*a* e *b*` o negrito é `a`, não `a* e *b`. Um `?` guloso prefere que o
  // grupo participe e engole o resto da linha.
  const miolo = '[^\\n\\s](?:[^\\n]*?[^\\n\\s])??'
  return {
    regex: new RegExp(`(^|[\\s(])${d}(${miolo})${d}`, 'g'),
    troca: `$1<${tag}>$2</${tag}>`,
  }
}

/**
 * As marcações que valem dentro de uma linha.
 *
 * Os pares duplos vêm antes dos simples: com `*` primeiro, `**negrito**`
 * viraria negrito vazio grudado no texto.
 */
const PARES = [
  par('||', 'tg-spoiler'),
  par('**', 'b'),
  par('__', 'u'),
  par('~~', 's'),
  par('*', 'b'),
  par('_', 'i'),
  par('~', 's'),
]

function aplicarInline(texto: string): string {
  let saida = texto
  for (const { regex, troca } of PARES) saida = saida.replace(regex, troca)
  return saida
}

/**
 * O texto e o `parse_mode` prontos para a Bot API.
 *
 * Sem marcação nenhuma o texto vai cru, **sem** `parse_mode`: mandar HTML
 * para um texto que ninguém formatou só criaria a chance de o Telegram
 * tropeçar em algo que ele leia como tag. Já quem escreveu `**oi**` recebe o
 * HTML escapado inteiro, onde as únicas tags são as que a conversão gerou.
 */
export function paraTelegram(entrada: string): { texto: string; parse_mode?: 'HTML' } {
  const html = markdownParaHtml(entrada)
  if (html === escaparHtml(entrada)) return { texto: entrada }
  return { texto: html, parse_mode: 'HTML' }
}

/**
 * O texto tem alguma marcação?
 *
 * A interface usa para decidir se mostra a prévia da formatação.
 */
export function temMarcacao(entrada: string): boolean {
  return paraTelegram(entrada).parse_mode !== undefined
}
